import { colorMap } from "./colors";
const money = ({ amount , currencyCode  })=>{
    return {
        value: +amount,
        currencyCode
    };
};
const normalizeProductOption = ({ id , name: displayName , values  })=>{
    return {
        __typename: "MultipleChoiceOption",
        id,
        displayName: displayName.toLowerCase(),
        values: values.map((value)=>{
            let output = {
                label: value
            };
            if (displayName.match(/colou?r/gi)) {
                const mappedColor = colorMap[value.toLowerCase().replace(/ /g, "")];
                if (mappedColor) {
                    output = {
                        ...output,
                        hexColors: [
                            mappedColor
                        ]
                    };
                }
            }
            return output;
        })
    };
};
const normalizeProductImages = ({ edges  })=>{
    return edges == null ? void 0 : edges.map(({ node: { url , originalSrc , ...rest }  })=>({
            url: url || originalSrc || "/",
            ...rest
        }));
};
const normalizeProductVariants = ({ edges  })=>{
    return edges == null ? void 0 : edges.map(({ node: { id , selectedOptions , sku , title , priceV2 , compareAtPriceV2 , requiresShipping , availableForSale , quantityAvailable ,  } ,  })=>{
        return {
            id,
            name: title,
            sku: sku ?? id,
            price: (priceV2 == null ? void 0 : priceV2.amount) != null ? +priceV2.amount : 0,
            listPrice: (compareAtPriceV2 == null ? void 0 : compareAtPriceV2.amount) != null ? +compareAtPriceV2.amount : 0,
            requiresShipping,
            availableForSale,
            quantityAvailable,
            options: (selectedOptions ?? []).map(({ name , value  })=>{
                const options = normalizeProductOption({
                    id,
                    name,
                    values: [
                        value
                    ]
                });
                return options;
            })
        };
    });
};
export function normalizeMetafields(metafields) {
    const fields = [];
    metafields == null ? void 0 : metafields.forEach((m)=>{
        var ref, ref1;
        if (!m) return;
        // Supports both edge-node shapes and flat metafield objects
        // (as returned by metafields(identifiers: [...])).
        const key = (m == null ? void 0 : (ref = m.node) == null ? void 0 : ref.key) ?? (m == null ? void 0 : m.key);
        const value = (m == null ? void 0 : (ref1 = m.node) == null ? void 0 : ref1.value) ?? (m == null ? void 0 : m.value);
        if (!key) return;
        fields.push({
            key,
            value
        });
    });
    return fields;
}
export function buildNamedMetafields(metafields) {
    const byKey = {};
    metafields == null ? void 0 : metafields.forEach((m)=>{
        var ref, ref2;
        if (!m) return;
        const key = (m == null ? void 0 : (ref = m.node) == null ? void 0 : ref.key) ?? (m == null ? void 0 : m.key);
        const value = (m == null ? void 0 : (ref2 = m.node) == null ? void 0 : ref2.value) ?? (m == null ? void 0 : m.value);
        if (!key || value == null) return;
        byKey[key] = value;
    });
    const parseGender = (raw)=>{
        if (!raw) return [];
        // Shopify list metafields are serialised as JSON arrays
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.map((v)=>String(v));
        } catch (_) {
        /* fall through */ }
        return [
            raw
        ];
    };
    return {
        gender: parseGender(byKey.gender),
        brand: byKey.brand ?? "",
        material: byKey.material ?? "",
        gemstone: byKey.gemstone ?? "",
        style: byKey.style ?? ""
    };
}
export function normalizeProduct({ id , title: name , vendor , images , variants , description , descriptionHtml , handle , priceRange , options , metafields , ...rest }) {
    var ref;
    return {
        id,
        name,
        vendor,
        path: `/${handle}`,
        slug: handle == null ? void 0 : handle.replace(/^\/+|\/+$/g, ""),
        price: {
            ...money(priceRange == null ? void 0 : priceRange.minVariantPrice),
            ...(priceRange == null ? void 0 : (ref = priceRange.maxVariantPrice) == null ? void 0 : ref.amount) !== undefined && {
                maxVariantPrice: +priceRange.maxVariantPrice.amount
            }
        },
        images: normalizeProductImages(images),
        variants: variants ? normalizeProductVariants(variants) : [],
        options: options ? options.filter((o)=>o.name !== "Title") // By default Shopify adds a 'Title' name when there's only one option. We don't need it. https://community.shopify.com/c/Shopify-APIs-SDKs/Adding-new-product-variant-is-automatically-adding-quot-Default/td-p/358095
        .map((o)=>normalizeProductOption(o)) : [],
        ...description && {
            description
        },
        ...descriptionHtml && {
            descriptionHtml
        },
        metafields: metafields ? normalizeMetafields(metafields) : [],
        namedMetafields: buildNamedMetafields(metafields),
        origin: {
            id,
            title: name,
            vendor,
            images,
            variants,
            description,
            descriptionHtml,
            handle,
            priceRange,
            options,
            metafields,
            ...rest
        },
        ...rest
    };
}
export function normalizeCollection({ id , title: name , description , descriptionHtml , handle , metafields , ...rest }) {
    return {
        id,
        name,
        path: `/${handle}`,
        slug: handle == null ? void 0 : handle.replace(/^\/+|\/+$/g, ""),
        ...description && {
            description
        },
        ...descriptionHtml && {
            descriptionHtml
        },
        metafields: metafields ? normalizeMetafields(metafields) : [],
        origin: {
            id,
            title: name,
            description,
            descriptionHtml,
            handle,
            metafields,
            ...rest
        },
        ...rest
    };
}
export function normalizeCart(cart) {
    var ref, ref3, ref4, ref5, ref6;
    return {
        id: cart.id,
        url: cart.checkoutUrl,
        customerId: "",
        email: "",
        createdAt: cart.createdAt,
        currency: {
            code: (ref = cart.cost.totalAmount) == null ? void 0 : ref.currencyCode
        },
        taxesIncluded: false,
        lineItems: (ref3 = cart.lines) == null ? void 0 : ref3.edges.map(normalizeLineItem),
        lineItemsSubtotalPrice: +((ref4 = cart.cost.subtotalAmount) == null ? void 0 : ref4.amount),
        subtotalPrice: +((ref5 = cart.cost.subtotalAmount) == null ? void 0 : ref5.amount),
        totalPrice: (ref6 = cart.cost.totalAmount) == null ? void 0 : ref6.amount,
        discounts: [],
        attributes: cart.attributes,
        note: (cart == null ? void 0 : cart.note) ?? ""
    };
}
function normalizeLineItem({ node: { id , merchandise , quantity , discountAllocations  }  }) {
    var ref, ref7, ref8, ref9;
    return {
        id,
        variantId: String(merchandise == null ? void 0 : merchandise.id),
        productId: String(merchandise == null ? void 0 : merchandise.product.id),
        name: `${merchandise.product.title}`,
        quantity,
        variant: {
            id: String(merchandise == null ? void 0 : merchandise.id),
            sku: (merchandise == null ? void 0 : merchandise.sku) ?? "",
            barcode: (merchandise == null ? void 0 : merchandise.barcode) ?? "",
            name: merchandise == null ? void 0 : merchandise.title,
            quantityAvailable: Number(merchandise == null ? void 0 : merchandise.quantityAvailable),
            image: {
                url: (merchandise == null ? void 0 : (ref = merchandise.image) == null ? void 0 : ref.url) || "/product-img-placeholder.svg"
            },
            requiresShipping: (merchandise == null ? void 0 : merchandise.requiresShipping) ?? false,
            price: merchandise == null ? void 0 : merchandise.price.amount,
            listPrice: merchandise == null ? void 0 : (ref7 = merchandise.compareAtPrice) == null ? void 0 : ref7.amount,
            productTags: (merchandise == null ? void 0 : (ref8 = merchandise.product) == null ? void 0 : ref8.tags) ?? []
        },
        path: String(merchandise == null ? void 0 : (ref9 = merchandise.product) == null ? void 0 : ref9.handle),
        discounts: [],
        discountAllocations,
        options: (merchandise == null ? void 0 : merchandise.title) == "Default Title" ? [] : merchandise == null ? void 0 : merchandise.selectedOptions
    };
}
export const normalizePage = ({ title: name , handle , ...page }, locale = "en-AU")=>({
        ...page,
        url: `${handle}`,
        name
    });
export const normalizePages = (edges, locale)=>{
    return edges == null ? void 0 : edges.map((edge)=>normalizePage(edge.node, locale));
};
export const normalizeCategory = ({ name , handle , id  })=>({
        id,
        name,
        slug: handle,
        path: `/${handle}`
    });
export const normalizeAlgoliaHit = ({ objectID , title , vendor , handle , body_html_safe , price , compare_at_price , tags , image , named_tags , product_type , ...rest }, currencyCode)=>{
    return {
        name: title,
        vendor,
        slug: handle,
        type: product_type,
        description: body_html_safe,
        path: `/product/${handle}`,
        variants: [],
        images: image ? [
            {
                url: image
            }, 
        ] : [],
        price: {
            value: price,
            currencyCode
        },
        tags,
        metafields: undefined,
        ...rest,
        id: objectID
    };
};
export function normalizeEdges(edges) {
    return edges.flatMap((edge)=>edge.node);
}
